import axios from './axios-with-authentication';
import * as backend from './backend-types';
import { DEVICE_ADAPTER_API_BASE_PATH } from './constants';
import { fetchDeviceListing, resolveDeviceType } from './device-listing';
import { DeviceType, SupportedDeviceType } from '../types';

// The device-adapter validates deviceType against a fixed enum. Send its canonical
// upper-case form rather than the app enum value (e.g. 'viQtor' is mixed-case),
// so we never depend on the backend being case-insensitive.
const DEVICE_ADAPTER_DEVICE_TYPE: Record<SupportedDeviceType, string> = {
  [DeviceType.Healthdot]: 'HEALTHDOT',
  [DeviceType.ViQtor]: 'VIQTOR',
};

export type PairingDevice = {
  id: string;
  serialNumber: string;
  assigned?: boolean;
  clientId?: string | null;
  /** As reported by the device-adapter listing, not inferred from the serial number. */
  deviceType?: DeviceType;
  deviceStatus?: backend.PortalDeviceStatus;
  batteryLevel?: number;
};

const BATTERY_LOW_THRESHOLD = 20;
const BATTERY_CRITICAL_THRESHOLD = 6;

function normalizeBatteryLevel(charge?: number | null): number | undefined {
  if (charge === undefined || charge === null) return undefined;

  const numeric = Number(charge);
  if (!Number.isFinite(numeric)) return undefined;

  const rounded = Math.round(numeric);
  if (rounded < 0 || rounded > 100) return undefined;

  return rounded;
}

export function mapDeviceAdapterStatusToPortalStatus(
  status: backend.DeviceAdapterDeviceStatusResponse,
): backend.PortalDeviceStatus {
  const connectionStatus = status.connectionStatus;
  const batteryStatus = status.batteryStatus;
  const wornStatus = status.wornStatus;
  const batteryLevel = normalizeBatteryLevel(batteryStatus?.charge);

  if (connectionStatus?.connected !== true) {
    if (connectionStatus?.lastUpdateTimestamp || connectionStatus?.disconnectionTimestamp) {
      return 'DISCONNECTED';
    }

    return 'UNKNOWN';
  }

  if (batteryStatus?.charging) {
    return 'CHARGING';
  }

  if (batteryLevel !== undefined) {
    if (batteryLevel < BATTERY_CRITICAL_THRESHOLD) return 'BATTERY_CRITICAL';
    if (batteryLevel < BATTERY_LOW_THRESHOLD) return 'BATTERY_LOW';
  }

  if (wornStatus?.worn === false) {
    return 'NOT_WORN';
  }

  return 'OK';
}

export async function createHealthdotDevice(
  serialNumber: string,
  hcoId: string,
): Promise<PairingDevice> {
  const payload: backend.DeviceAdapterCreateDeviceRequest = {
    serialNumber,
    healthCareOrganizationId: hcoId,
    // This path only ever creates Healthdots.
    deviceType: DEVICE_ADAPTER_DEVICE_TYPE[DeviceType.Healthdot],
  };
  const { data: createdDevice } = await axios.post<backend.DeviceAdapterDeviceResponse>(
    `${DEVICE_ADAPTER_API_BASE_PATH}/devices`,
    payload,
  );

  return {
    id: createdDevice.id,
    serialNumber: createdDevice.serialNumber || serialNumber,
    assigned: createdDevice.assigned,
    clientId: createdDevice.clientId ?? null,
  };
}

export async function getDevicesForPairing(hcoId: string): Promise<PairingDevice[]> {
  // Resolve scanned devices by listing HCO devices and matching serial numbers. The
  // device-adapter listing is the one that states each device's type, so the pairing flow
  // no longer has to infer it from the shape of the serial number. No `pairedSince`: a
  // scanned device has to be found whenever it was last paired.
  const devices = await fetchDeviceListing({ hcoId });

  return devices.map((device) => ({
    id: device.id,
    serialNumber: device.serialNumber,
    assigned: device.assigned,
    clientId: device.clientId,
    deviceType: resolveDeviceType(device),
    deviceStatus: mapDeviceAdapterStatusToPortalStatus({
      connectionStatus: device.connectionStatus,
      batteryStatus: device.batteryStatus,
      wornStatus: device.wornStatus,
    }),
    batteryLevel: normalizeBatteryLevel(device.batteryStatus?.charge),
  }));
}

export async function getDeviceStatusForPairing(
  deviceId: string,
): Promise<Pick<PairingDevice, 'deviceStatus' | 'batteryLevel'>> {
  const { data: status } = await axios.get<backend.DeviceAdapterDeviceStatusResponse>(
    `${DEVICE_ADAPTER_API_BASE_PATH}/devices/${deviceId}/status`,
  );

  return {
    deviceStatus: mapDeviceAdapterStatusToPortalStatus(status),
    batteryLevel: normalizeBatteryLevel(status.batteryStatus?.charge),
  };
}
